<template>
  <div ref="containerRef" class="virtual-list-container" @scroll="handleScroll">
    <!-- 占位容器，撑起总高度（header + 内容高度 + footer 高度） -->
    <div class="virtual-list-placeholder" :style="{ height: totalHeightWithHeaderAndFooter + 'px' }">
      <!-- 🔧 Header 插槽：使用绝对定位，固定在最顶部 -->
      <div class="virtual-list-header" :style="{ top: '0px' }" ref="headerRef" v-if="$slots.header">
        <slot name="header"></slot>
      </div>

      <!-- 渲染可见区域的项目 -->
      <div class="virtual-list-items" :style="{ transform: `translateY(${offsetY + headerHeight}px)` }">
        <div v-for="item in visibleItems" :key="getItemKey(item)" :data-index="item.index" class="virtual-list-item">
          <slot name="item" :item="item.data" :index="item.index"></slot>
        </div>
        <div class="virtual-list-loading" v-if="$slots.loading">
          <slot name="loading"></slot>
        </div>
      </div>


      <!-- 🔧 Footer 插槽：使用绝对定位，固定在列表内容高度之后 -->
      <div class="virtual-list-footer" :style="{ top: totalHeight + headerHeight + 100 + 'px' }" ref="footerRef"
        v-if="$slots.footer">
        <slot name="footer"></slot>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'

interface Props {
  items: any[]
  itemHeight?: number // 预估的项目高度
  buffer?: number // 缓冲区数量（上下各多渲染几个）
  keyField?: string // 用作 key 的字段名
}

interface Emits {
  (e: 'scroll', event: Event): void
  (e: 'reach-bottom'): void
}

const props = withDefaults(defineProps<Props>(), {
  itemHeight: 80, // 默认高度 80px
  buffer: 3, // 默认上下各多渲染 3 个
  keyField: 'id'
})

const emit = defineEmits<Emits>()

const containerRef = ref<HTMLElement>()
const headerRef = ref<HTMLElement>()
const footerRef = ref<HTMLElement>()
const scrollTop = ref(0)
const containerHeight = ref(0)
const itemHeights = ref<Map<number, number>>(new Map())
const itemOffsets = ref<Map<number, number>>(new Map())
const headerHeight = ref(0)
const footerHeight = ref(0)

// 计算总高度
const totalHeight = computed(() => {
  let height = 0
  for (let i = 0; i < props.items.length; i++) {
    height += itemHeights.value.get(i) || props.itemHeight
  }
  return height
})

// 计算总高度（包含 header + footer）
const totalHeightWithHeaderAndFooter = computed(() => {
  return headerHeight.value + totalHeight.value + footerHeight.value
})

// 获取项目的 key
const getItemKey = (item: { data: any; index: number }) => {
  const data = item.data as any
  return data[props.keyField] || item.index
}

// 计算每个项目的偏移量
const updateOffsets = () => {
  let offset = 0
  for (let i = 0; i < props.items.length; i++) {
    itemOffsets.value.set(i, offset)
    offset += itemHeights.value.get(i) || props.itemHeight
  }
}

// 查找第一个可见项的索引
const findStartIndex = (): number => {
  let low = 0
  let high = props.items.length - 1
  // 🔧 考虑 header 高度，计算相对于内容区的 scrollTop
  const contentScrollTop = Math.max(0, scrollTop.value - headerHeight.value)

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const offset = itemOffsets.value.get(mid) || 0

    if (offset === contentScrollTop) {
      return mid
    } else if (offset < contentScrollTop) {
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  return Math.max(0, low - 1)
}

// 计算可见项目
const visibleItems = computed(() => {
  if (props.items.length === 0 || containerHeight.value === 0) {
    return []
  }

  updateOffsets()

  const startIndex = findStartIndex()
  const items: Array<{ data: any; index: number }> = []

  let currentOffset = itemOffsets.value.get(startIndex) || 0
  let index = startIndex

  // 🔧 考虑 header 高度，计算相对于内容区的 scrollTop
  const contentScrollTop = Math.max(0, scrollTop.value - headerHeight.value)

  // 添加缓冲区前的项目
  const bufferStart = Math.max(0, startIndex - props.buffer)
  for (let i = bufferStart; i < startIndex; i++) {
    items.push({
      data: props.items[i],
      index: i
    })
  }

  // 添加可见区域的项目
  while (
    currentOffset < contentScrollTop + containerHeight.value &&
    index < props.items.length
  ) {
    items.push({
      data: props.items[index],
      index
    })
    currentOffset += itemHeights.value.get(index) || props.itemHeight
    index++
  }

  // 添加缓冲区后的项目
  const bufferEnd = Math.min(props.items.length, index + props.buffer)
  for (let i = index; i < bufferEnd; i++) {
    items.push({
      data: props.items[i],
      index: i
    })
  }

  return items
})

// 计算渲染偏移量
const offsetY = computed(() => {
  if (visibleItems.value.length === 0) return 0
  const firstIndex = visibleItems.value[0].index
  return itemOffsets.value.get(firstIndex) || 0
})

// 处理滚动
const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement
  scrollTop.value = target.scrollTop

  emit('scroll', event)

  // 检测是否到达底部
  const scrollHeight = target.scrollHeight
  const clientHeight = target.clientHeight
  const distanceToBottom = scrollHeight - scrollTop.value - clientHeight

  if (distanceToBottom < 100) {
    emit('reach-bottom')
  }
}

// 测量项目实际高度
const measureItemHeights = () => {
  if (!containerRef.value) return

  const items = containerRef.value.querySelectorAll('.virtual-list-item')
  let needsUpdate = false

  items.forEach((element) => {
    const index = parseInt(element.getAttribute('data-index') || '-1')
    if (index >= 0) {
      const height = element.getBoundingClientRect().height
      const currentHeight = itemHeights.value.get(index)

      if (currentHeight !== height && height > 0) {
        itemHeights.value.set(index, height)
        needsUpdate = true
      }
    }
  })

  if (needsUpdate) {
    updateOffsets()
  }
}

// 监听容器大小
const updateContainerHeight = () => {
  if (containerRef.value) {
    containerHeight.value = containerRef.value.clientHeight
  }
}

// 🔧 测量 header 高度
const measureHeaderHeight = () => {
  if (headerRef.value) {
    const height = headerRef.value.getBoundingClientRect().height
    if (height > 0 && height !== headerHeight.value) {
      headerHeight.value = height
    }
  }
}

// 🔧 测量 footer 高度
const measureFooterHeight = () => {
  if (footerRef.value) {
    const height = footerRef.value.getBoundingClientRect().height
    if (height > 0 && height !== footerHeight.value) {
      footerHeight.value = height
    }
  }
}

// 监听数据变化
watch(
  () => props.items.length,
  () => {
    nextTick(() => {
      measureItemHeights()
    })
  }
)

// 观察项目高度变化
let resizeObserver: ResizeObserver | null = null

const setupResizeObserver = () => {
  if (!containerRef.value || typeof ResizeObserver === 'undefined') return

  resizeObserver = new ResizeObserver(() => {
    measureItemHeights()
    measureHeaderHeight()
    measureFooterHeight()
  })

  nextTick(() => {
    const items = containerRef.value?.querySelectorAll('.virtual-list-item')
    items?.forEach(item => {
      resizeObserver?.observe(item)
    })

    // 🔧 也观察 header 和 footer
    if (headerRef.value) {
      resizeObserver?.observe(headerRef.value)
    }
    if (footerRef.value) {
      resizeObserver?.observe(footerRef.value)
    }
  })
}

onMounted(() => {
  updateContainerHeight()
  window.addEventListener('resize', updateContainerHeight)

  nextTick(() => {
    measureItemHeights()
    measureHeaderHeight()
    measureFooterHeight()
    setupResizeObserver()
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', updateContainerHeight)
  resizeObserver?.disconnect()
})

// 监听可见项变化，重新观察新渲染的元素
watch(visibleItems, () => {
  nextTick(() => {
    if (resizeObserver) {
      resizeObserver.disconnect()
      setupResizeObserver()
    }
  })
})

// 暴露方法
defineExpose({
  scrollTo: (offset: number) => {
    if (containerRef.value) {
      containerRef.value.scrollTop = offset
    }
  },
  scrollToIndex: (index: number) => {
    const offset = itemOffsets.value.get(index) || 0
    if (containerRef.value) {
      containerRef.value.scrollTop = offset
    }
  }
})
</script>

<style lang="scss" scoped>
.virtual-list-container {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}

.virtual-list-placeholder {
  position: relative;
  width: 100%;
}

.virtual-list-items {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  will-change: transform;
}

.virtual-list-item {
  width: 100%;
}

.virtual-list-header {
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
}

.virtual-list-footer {
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
}
</style>
